import { castHubCardJSONResponse } from '@/utils/insights-hub';
import { getDeviceTimezone } from '@/utils/date';
import {
  GetHubCardDataQuery,
  GetHubCardDataQueryVariables,
  HubCardType,
} from '@/types';
import { gql, WatchQueryFetchPolicy } from '@apollo/client';
import { partnerSupplier } from '@/utils/environment';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';

export const GET_HUB_CARD_DATA_QUERY = gql`
  query getHubCardData(
    $businessOrganizationUUID: uuid!
    $cardType: HubCardType!
    $userUUID: uuid!
    $userTimezone: String!
  ) {
    getHubCardData(
      analytics_type: $cardType
      business_organization_id: $businessOrganizationUUID
      user_id: $userUUID
      user_timezone: $userTimezone
    ) {
      card_type
      card_data_json
    }
  }
`;

/**
 * Options for the useHubCardData hook data fetching
 *
 * @param fetchPolicy - The fetch policy to use for the query. Defaults to network-only. See https://www.apollographql.com/docs/react/data/queries#setting-a-fetch-policy for more details.
 * @param skip - Whether to skip the query. Defaults to false. See https://www.apollographql.com/docs/react/data/queries#queryhookoptions-interface-skip for more details.
 */
export interface HubCardDataOptions {
  fetchPolicy?: WatchQueryFetchPolicy;
  skip?: boolean;
}

export const useHubCardData = <T extends HubCardType>(
  cardType: T,
  options?: HubCardDataOptions,
) => {
  const { user } = useAuthenticatedUserContext();
  const { data, loading, error } = useWrappedQuery<
    GetHubCardDataQuery,
    GetHubCardDataQueryVariables
  >(GET_HUB_CARD_DATA_QUERY, {
    context: {
      noBatch: true,
    },
    fetchPolicy: options?.fetchPolicy ?? 'network-only',
    skip: options?.skip ?? false,
    variables: {
      businessOrganizationUUID: partnerSupplier.businessOrganizationUUID,
      cardType,
      userTimezone: getDeviceTimezone(),
      userUUID: user.uuid,
    },
  });

  return {
    data: data
      ? castHubCardJSONResponse<T>(
          {
            cardDataJson: data.getHubCardData.card_data_json,
            cardType: data.getHubCardData.card_type,
          },
          cardType,
        )
      : undefined,
    error,
    loading,
  };
};
